import { faSignalStream, faSignalAltSlash } from '@/assets/icons/pro-solid.js'
import { DateTime, Interval } from 'luxon'
import { eventTypes } from '@/helpers/event-helpers'
import * as alertTypes from '@/helpers/alert-types'

/**
 * @description Formats the event start and end date to render in the tooltip
 * @param {DateTime} start a Luxon DateTime representing the start of the event
 * @param {DateTime} end a Luxon DateTime representing the end of the event
 * @returns a string specifying the start and end date of the event
 */
export const eventDateRange = (start, end) => {
  const formatDateString = 'd LLL yyyy'
  const startDate = start.toFormat(formatDateString)
  const endDate = end ? end.toFormat(formatDateString) : null
  if (!endDate || (startDate && (startDate === endDate))) return `${startDate}`
  return `${startDate} - ${endDate}`
}

/**
 * @description Formats the event start and end time to render in the tooltip
 * @param {DateTime} start a Luxon DateTime representing the start of the event
 * @param {DateTime} end a Luxon DateTime representing the end of the event
 * @returns a string specifying the start and end time of the event
 */
export const eventTimeRange = (start, end) => {
  const formatTimeString = 'h:mma'
  const startTime = start.toFormat(formatTimeString)
  if (!end) return `${startTime}`
  const endTime = end.toFormat(formatTimeString)
  return `${startTime} - ${endTime}`
}

/**
 * @description An HTML string for FontAwesome icons in the tooltip
 * @param {String} type The type of icon to render: either 'noise' or 'disconnect'
 */
const iconHTML = (type) => {
  const iconObj = type.toUpperCase() === eventTypes.NOISE ? faSignalStream : faSignalAltSlash
  return (
    `
    <span class="v-icon notranslate theme--light">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        class="svg-inline--fa fa-${iconObj.iconName} fa-w-16 v-icon__svg theme--light"
        data-prefix="${iconObj.prefix}"
        data-icon="${iconObj.iconName}"
        viewBox="0 0 ${iconObj.icon.slice(0, 2).join(' ')}"
        color="white"
      >
        <path class="" fill="currentColor" d="${iconObj.icon[4]}"></path>
      </svg>
    </span>
    `
  )
}

export const eventTooltipHTML = (type, start, end, activityZone) => {
  return (
  `<div class="chartjs-custom-tooltip">
    <div class="chartjs-custom-tooltip-header">
      <div class="chartjs-custom-tooltip-header__icon">
        ${iconHTML(type)}
      </div>
      <div class="chartjs-custom-tooltip-header__text">
        ${type.toUpperCase()} Event
      </div>
    </div>
  <div class="chartjs-custom-tooltip-body">
    <div class="chartjs-custom-tooltip-body__date">
      ${eventDateRange(start, end)}
    </div>
    <div class="chartjs-custom-tooltip-body__details">
      ${eventTimeRange(start, end)}
    </div>
    <div class="chartjs-custom-tooltip-body__details">
      ${activityZone}
    </div>
  </div>
</div>`
  )
}

const alertTooltipHTML = (date, time, alertType) => {
  const alertTypeFormatter = alertType => {
    switch (alertType) {
      case alertTypes.FIRST_ACTIONABLE_NOISE:
      case alertTypes.FIRST_AUTO_RESOLVE_GUEST:
        return '1st Alert Sent'
      case alertTypes.SECOND_ACTIONABLE_NOISE:
      case alertTypes.SECOND_AUTO_RESOLVE_GUEST:
        return '2nd Alert Sent'
      case alertTypes.THIRD_ACTIONABLE_NOISE:
      case alertTypes.THIRD_AUTO_RESOLVE_PM:
        return '3rd Alert Sent'
      default:
        return 'Alert Sent'
    }
  }
  return (
    `<div class="chartjs-alert-tooltip">
      <div class="chartjs-alert-tooltip-header">${date}
      <br/>${time}
      </div>
      <div class="chartjs-alert-tooltip-item">${alertTypeFormatter(alertType)}</div>
    </div>`
  )
}

/**
 * @description A higher-order function that takes a canvas element and an array of events and produces
 *              a custom tooltip callback function; to be passed to the `custom` property on the ChartJS
 *              tooltips config object
 * @param {HTMLElement} canvas The canvas element where the tooltip is to be drawn
 * @param {Array} eventRanges An array of objects representing events for the tooltip.
 *                            Each object should have { type, start, end, activityZone }
 * @param {Array} alertPoints An array of objects representing alerts for the tooltip.
 *                            Each object should have { timestamp, activityZone, type }
 * @returns a custom tooltip callback function
 */
const GraphTooltip = (canvas, eventRanges, alertPoints, timezone) => (tooltipModel) => {
  handleEventTooltips(canvas, tooltipModel, eventRanges)
  handleAlertTooltips(canvas, tooltipModel, alertPoints, timezone)
}

const handleEventTooltips = (canvas, tooltipModel, eventRanges) => {
  const customEventTooltipId = 'chartjs-event-tooltip'
  let eventTooltipEl = document.getElementById(customEventTooltipId)

  if (!eventTooltipEl) {
    eventTooltipEl = document.createElement('div')
    eventTooltipEl.id = customEventTooltipId
    canvas.insertAdjacentElement('afterend', eventTooltipEl)
  }
  // positions the element relative to the parent element of canvas
  eventTooltipEl.style.position = 'absolute'
  eventTooltipEl.style.left = tooltipModel.caretX + 'px'
  eventTooltipEl.style.top = tooltipModel.caretY + 'px'

  const timestampAtCursor = tooltipModel.title ? tooltipModel.title[0] : ''

  if (eventRanges && eventRanges.length && timestampAtCursor) {
    const eventsAtCursor = eventRanges.filter(eventRange => intersectsEvent(timestampAtCursor, eventRange))
    if (eventsAtCursor.length) {
      const { type, start, end, activityZone } = eventsAtCursor[0]
      eventTooltipEl.style.opacity = 1
      eventTooltipEl.innerHTML = eventTooltipHTML(type, DateTime.fromISO(start, { setZone: true }), end ? DateTime.fromISO(end, { setZone: true }) : undefined, activityZone)
    } else eventTooltipEl.style.opacity = 0
  }
}

const handleAlertTooltips = (canvas, tooltipModel, alertPoints, timezone) => {
  const customAlertTooltipId = 'chartjs-alert-tooltip'
  let alertTooltipEl = document.getElementById(customAlertTooltipId)

  if (!alertTooltipEl) {
    alertTooltipEl = document.createElement('div')
    alertTooltipEl.id = customAlertTooltipId
    canvas.insertAdjacentElement('afterend', alertTooltipEl)
  }
  // positions the element relative to the parent element of canvas
  alertTooltipEl.style.position = 'absolute'
  alertTooltipEl.style.left = tooltipModel.caretX - 64 + 'px'
  alertTooltipEl.style.top = tooltipModel.caretY - 84 + 'px'

  let timestampAtCursor = tooltipModel.title ? tooltipModel.title[0] : ''
  if (alertPoints && alertPoints.length && timestampAtCursor) {
    timestampAtCursor = DateTime.fromISO(timestampAtCursor, { setZone: true }).toUTC().toISO()
    const alertAtCursor = alertPoints.filter(alertPoint => nearAlert(timestampAtCursor, alertPoint.timestamp, 2))
    if (alertAtCursor.length) {
      const { type, timestamp } = alertAtCursor[0]
      const date = DateTime.fromISO(timestamp).setZone(timezone).toFormat('LLL dd, yyyy')
      const time = DateTime.fromISO(timestamp).setZone(timezone).toFormat('h:mm a')
      alertTooltipEl.style.opacity = 1
      alertTooltipEl.innerHTML = alertTooltipHTML(date, time, type)
    } else alertTooltipEl.style.opacity = 0
  } else alertTooltipEl.style.opacity = 0
}

/**
 * @description computes whether the given timestamp occurs during an event
 * @param {String} timestamp a ISO 8601-compliant date string
 * @param {Object} eventRange an object representing the event
 *  @param {String} eventRange.start an ISO 8601-compliant date string marking the start of the event
 *  @param {String} eventRange.end an ISO 8601-compliant date string marking the end of the event
 * @returns Boolean; true if the timestamp occurs during the eventRange
 */
const intersectsEvent = (timestamp, eventRange) => {
  const eventInterval = Interval.fromISO(`${eventRange.start}/${eventRange.end || DateTime.local().toISO()}`)
  return eventInterval.contains(DateTime.fromISO(timestamp, { setZone: true })) || (eventRange.end && timestamp === eventRange.end)
}

/**
 * @description computes whether the given timestamp is near an alert
 * @param {String} timestamp a ISO 8601-compliant date string
 * @param {String} alertTime a ISO 8601-compliant date string
 * @param {Number} margin the maximum allowed difference, in minutes, between timestamp and alertTime
 * @returns Boolean; true if the timestamp is within margin of the alertTime
 */
export const nearAlert = (timestamp, alertTime, margin) => {
  const diff = DateTime.fromISO(timestamp).diff(DateTime.fromISO(alertTime), 'minutes')
  if (Math.abs(diff.as('minutes')) < margin) return true
  return false
}

export default GraphTooltip
